/**
 * Layout — sidebar codex-style + topbar cu breadcrumb/search/acțiuni.
 *
 * Structura (inspirată din docs/codex-password-manager-v2.html):
 * - Sidebar 240px: brand cu mark + serif name + badge, search ⌘K,
 *   nav-sections (Navigare + Spaces), footer avatar + theme toggle
 * - Topbar 52px: breadcrumb cu iconiță route + search (doar pe /notes /vault),
 *   acțiuni (settings + logout)
 * - Main: padding 32px sau full-width pe /notes
 */
import { useEffect, useState, type FC } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/lib/useIsMobile';
import { authApi } from '@/api/auth';
import type { TenantMembership } from '@/api/auth';
import CommandPalette from '@/components/CommandPalette';
import SessionTimer from '@/components/SessionTimer';
import InviteModal from '@/features/account/InviteModal';
import {
  AlertIcon,
  ChartIcon,
  ChevronLeftIcon,
  ClockIcon,
  FolderIcon,
  KeyIcon,
  LogoutIcon,
  MoonIcon,
  NoteIcon,
  PlusIcon,
  SearchIcon,
  SettingsIcon,
  ShareIcon,
  ShieldIcon,
  SunIcon,
  TrashIcon,
} from '@/features/notes/icons';
import { useAuthStore } from '@/stores/auth';
import { useLayoutStore } from '@/stores/layout';
import { useThemeStore } from '@/stores/theme';
import RecoveryReminder from '@/components/RecoveryReminder';
import { IconProps, HamburgerIcon, CloseIcon, BrandMark } from '@/components/layout/icons';
import { CreateSpaceModal } from '@/components/layout/CreateSpaceModal';

interface NavItem {
  to: string;
  labelKey: string;
  Icon: FC<IconProps>;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/vault',         labelKey: 'nav.vault',         Icon: KeyIcon },
  { to: '/authenticator', labelKey: 'nav.authenticator', Icon: ClockIcon },
  { to: '/notes',         labelKey: 'nav.notes',         Icon: NoteIcon },
  { to: '/security',      labelKey: 'nav.security',      Icon: ShieldIcon },
  { to: '/break-glass',   labelKey: 'nav.break_glass',   Icon: AlertIcon },
  { to: '/audit',         labelKey: 'nav.audit',         Icon: ChartIcon },
  { to: '/trash',         labelKey: 'nav.trash',         Icon: TrashIcon },
  { to: '/settings',      labelKey: 'nav.settings',      Icon: SettingsIcon },
];

const TENANT_COLORS = [
  'var(--accent)',
  '#7f77dd',
  '#ef9f27',
  '#d4537e',
  '#3b82f6',
  '#14b8a6',
];

export default function Layout() {
  const { t } = useTranslation();
  const { user, currentTenantId, setTenant, addTenant, logout } = useAuthStore();
  const themeMode = useThemeStore((s) => s.mode);
  const setThemeMode = useThemeStore((s) => s.setMode);
  const rawSidebarCollapsed = useLayoutStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useLayoutStore((s) => s.toggleSidebar);
  const navigate = useNavigate();
  const location = useLocation();
  const fullWidth = location.pathname.startsWith('/notes');
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [createSpaceOpen, setCreateSpaceOpen] = useState(false);

  // Lista de spații din sesiune poate fi veche (invitație acceptată în alt
  // tab, eliminare, rol schimbat, spațiu șters) — o aliniem cu serverul la
  // încărcare și când tab-ul revine în față.
  const syncTenants = useAuthStore((s) => s.syncTenants);
  useEffect(() => {
    const sync = () => {
      if (document.visibilityState !== 'visible') return;
      authApi
        .me()
        .then((me) => syncTenants(me.tenants))
        .catch(() => {
          // Best-effort; 401 e tratat global de client.
        });
    };
    sync();
    document.addEventListener('visibilitychange', sync);
    return () => document.removeEventListener('visibilitychange', sync);
  }, [syncTenants]);
  const [inviteTenant, setInviteTenant] = useState<TenantMembership | null>(null);
  const isMobile = useIsMobile();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Pe mobile, sidebar-ul e drawer peste conținut — ignorăm starea de
  // "collapsed" (care are sens doar pe desktop, ca rail îngust).
  const sidebarCollapsed = isMobile ? false : rawSidebarCollapsed;

  // Închide drawer-ul la navigare între pagini.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Blochează scroll-ul body-ului când drawer-ul e deschis.
  useEffect(() => {
    if (!isMobile || !mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMobile, mobileOpen]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
      if (e.key === 'Escape' && mobileOpen) {
        setMobileOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const currentNav = NAV_ITEMS.find((n) => location.pathname.startsWith(n.to));
  const avatarInitials = (user?.email ?? '?').slice(0, 2).toUpperCase();

  const resolvedTheme =
    themeMode === 'system'
      ? typeof window !== 'undefined' &&
        window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light'
      : themeMode;

  const toggleTheme = () => {
    setThemeMode(resolvedTheme === 'dark' ? 'light' : 'dark');
  };

  return (
    <div
      className="h-screen grid"
      style={{
        gridTemplateColumns: isMobile
          ? '1fr'
          : `${sidebarCollapsed ? '56px' : '240px'} 1fr`,
        transition: 'grid-template-columns 220ms cubic-bezier(0.2, 0, 0, 1)',
      }}
    >
      {/* Backdrop pentru drawer mobile */}
      {isMobile && mobileOpen && (
        <div
          className="fixed inset-0 z-40"
          style={{
            background: 'rgba(0,0,0,0.45)',
            backdropFilter: 'blur(2px)',
          }}
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* ============ SIDEBAR ============ */}
      <aside
        className="flex flex-col overflow-hidden"
        data-collapsed={sidebarCollapsed ? 'true' : 'false'}
        style={{
          background: 'var(--bg-elevated)',
          borderRight: '1px solid var(--border)',
          ...(isMobile
            ? {
                position: 'fixed',
                top: 0,
                bottom: 0,
                left: 0,
                width: 260,
                zIndex: 50,
                transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)',
                transition: 'transform 260ms cubic-bezier(0.2, 0, 0, 1)',
                boxShadow: mobileOpen ? '0 12px 40px rgba(0,0,0,0.35)' : undefined,
              }
            : {}),
        }}
      >
        {/* Brand */}
        <div
          className={`flex items-center ${
            sidebarCollapsed ? 'flex-col gap-0 py-3 px-0' : 'gap-2.5 px-5 py-4'
          }`}
          style={{ borderBottom: '1px solid var(--border)', minHeight: 60 }}
        >
          <BrandMark />
          {!sidebarCollapsed && (
            <>
              <span
                className="font-serif text-[19px]"
                style={{ letterSpacing: '-0.01em', color: 'var(--text)' }}
              >
                Cerbes
              </span>
              <span
                className="ml-auto font-mono uppercase"
                style={{
                  fontSize: '9px',
                  letterSpacing: '0.15em',
                  padding: '2px 6px',
                  border: '1px solid var(--border-strong)',
                  borderRadius: '3px',
                  color: 'var(--c-mint)',
                  background: 'var(--bg-mint)',
                }}
              >
                v0.1
              </span>
            </>
          )}
          {isMobile ? (
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="grid place-items-center rounded-md w-[26px] h-[26px]"
              style={{ color: 'var(--text-dim)', border: '1px solid transparent' }}
              aria-label={t('shell_ui.layout.close_menu')}
              title={t('common.close')}
            >
              <CloseIcon className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={toggleSidebar}
              className={`grid place-items-center rounded-md transition-colors ${
                sidebarCollapsed ? 'mt-2.5 w-[26px] h-[26px]' : 'ml-1 w-[26px] h-[26px]'
              }`}
              style={{ color: 'var(--text-dim)', border: '1px solid transparent' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--bg-hover)';
                e.currentTarget.style.color = 'var(--text)';
                e.currentTarget.style.borderColor = 'var(--border-strong)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'var(--text-dim)';
                e.currentTarget.style.borderColor = 'transparent';
              }}
              title={sidebarCollapsed ? t('shell_ui.layout.expand') : t('shell_ui.layout.collapse')}
              aria-label={
                sidebarCollapsed
                  ? t('shell_ui.layout.expand_sidebar')
                  : t('shell_ui.layout.collapse_sidebar')
              }
            >
              <ChevronLeftIcon
                className="w-4 h-4"
                style={{
                  transform: sidebarCollapsed ? 'rotate(180deg)' : 'rotate(0)',
                  transition: 'transform 220ms cubic-bezier(0.2, 0, 0, 1)',
                }}
              />
            </button>
          )}
        </div>

        {/* Search ⌘K */}
        <div
          className={sidebarCollapsed ? 'p-2' : 'p-3'}
          style={{ borderBottom: '1px solid var(--border)' }}
        >
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            className={`w-full flex items-center gap-2 rounded-md text-[13px] transition-colors ${
              sidebarCollapsed ? 'justify-center p-2' : 'px-2.5 py-2'
            }`}
            style={{
              background: 'var(--bg)',
              border: '1px solid var(--border-strong)',
              color: 'var(--text-dim)',
            }}
            title={sidebarCollapsed ? `${t('common.search')} ⌘K` : undefined}
          >
            <SearchIcon className="w-3.5 h-3.5 flex-shrink-0" />
            {!sidebarCollapsed && (
              <>
                <span className="flex-1 text-left">{t('common.search')}...</span>
                <span className="kbd">⌘K</span>
              </>
            )}
          </button>
        </div>

        {/* Nav: Navigare */}
        <nav className={`${sidebarCollapsed ? 'px-1.5 pt-2' : 'px-2 pt-3'} pb-1 flex-1 overflow-y-auto`}>
          {!sidebarCollapsed && (
            <div className="px-3 pb-2 section-label no-dot">
              <span>{t('nav.navigation')}</span>
            </div>
          )}
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              title={sidebarCollapsed ? t(item.labelKey) : undefined}
              className={({ isActive }) =>
                `flex items-center rounded text-[13px] transition-colors cursor-pointer select-none ${
                  sidebarCollapsed ? 'justify-center p-2' : 'gap-2.5 px-3 py-1.5'
                } ${isActive ? 'sidebar-nav-active' : 'sidebar-nav-idle'}`
              }
            >
              {({ isActive }) => (
                <>
                  <item.Icon
                    className={`w-4 h-4 flex-shrink-0 ${
                      isActive ? 'nav-icon-active' : 'nav-icon-idle'
                    }`}
                  />
                  {!sidebarCollapsed && <span>{t(item.labelKey)}</span>}
                </>
              )}
            </NavLink>
          ))}

          {/* Spaces */}
          {user && (
            <div className={sidebarCollapsed ? 'mt-3 pt-3 border-t border-[var(--border)]' : 'mt-6'}>
              {!sidebarCollapsed && (
                <div className="px-3 pb-2 section-label no-dot">
                  <span>{t('nav.spaces')}</span>
                  <button
                    type="button"
                    onClick={() => setCreateSpaceOpen(true)}
                    className="w-3.5 h-3.5 grid place-items-center rounded transition-colors"
                    style={{ color: 'var(--text-dim)' }}
                    title={t('nav.create_space')}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = 'var(--bg-hover)')
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = 'transparent')
                    }
                  >
                    <PlusIcon className="w-3 h-3" />
                  </button>
                </div>
              )}
              {user.tenants.map((tenant, idx) => {
                const active = tenant.tenant_id === currentTenantId;
                const color = TENANT_COLORS[idx % TENANT_COLORS.length];
                const canInvite = tenant.role === 'owner' || tenant.role === 'admin';
                return (
                  <div
                    key={tenant.tenant_id}
                    className={`group flex items-center rounded text-[13px] transition-colors ${
                      active ? 'sidebar-nav-active' : 'sidebar-nav-idle'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setTenant(tenant.tenant_id)}
                      title={sidebarCollapsed ? tenant.name : undefined}
                      className={`flex-1 min-w-0 flex items-center cursor-pointer select-none ${
                        sidebarCollapsed ? 'justify-center p-2' : 'gap-2.5 px-3 py-1.5'
                      }`}
                    >
                      <FolderIcon className="w-4 h-4 flex-shrink-0" style={{ color }} />
                      {!sidebarCollapsed && <span className="truncate">{tenant.name}</span>}
                    </button>
                    {!sidebarCollapsed && canInvite && (
                      <button
                        type="button"
                        onClick={() => setInviteTenant(tenant)}
                        className={`mr-1.5 p-1 rounded hover:bg-[var(--bg-hover)] focus:opacity-100 group-hover:opacity-100 ${
                          active ? 'opacity-70' : 'opacity-0'
                        }`}
                        style={{ color: 'var(--text-dim)' }}
                        title={t('tenant_invites.share_space', { name: tenant.name })}
                        aria-label={t('tenant_invites.share_space', { name: tenant.name })}
                      >
                        <ShareIcon className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </nav>

        {/* Footer */}
        <div
          className={`flex items-center ${
            sidebarCollapsed ? 'flex-col gap-1.5 px-1.5 py-2.5' : 'gap-2.5 p-2.5'
          }`}
          style={{ borderTop: '1px solid var(--border)' }}
        >
          <button
            type="button"
            onClick={() => navigate('/account')}
            title={sidebarCollapsed ? `${t('account.title')} · ${user?.email ?? ''}` : t('account.title')}
            className={`flex items-center rounded transition-colors ${
              sidebarCollapsed ? 'p-0' : 'gap-2.5 flex-1 min-w-0 p-1 -m-1'
            }`}
            onMouseEnter={(e) => {
              if (!sidebarCollapsed) {
                e.currentTarget.style.background = 'var(--bg-hover)';
              }
            }}
            onMouseLeave={(e) => {
              if (!sidebarCollapsed) {
                e.currentTarget.style.background = 'transparent';
              }
            }}
          >
            <div
              className="w-[30px] h-[30px] rounded-full grid place-items-center font-semibold text-[11px] flex-shrink-0"
              style={{
                background: 'var(--grad-mint)',
                color: '#042c21',
                boxShadow: 'var(--glow-mint)',
              }}
            >
              {avatarInitials}
            </div>
            {!sidebarCollapsed && (
              <div className="flex-1 min-w-0 text-left">
                <div className="text-[12.5px] font-medium truncate" style={{ color: 'var(--text)' }}>
                  {user?.email?.split('@')[0] ?? t('shell_ui.layout.anonymous')}
                </div>
                <div
                  className="text-[11px] font-mono truncate"
                  style={{ color: 'var(--text-dim)' }}
                >
                  {user?.email ?? t('shell_ui.layout.not_logged_in')}
                </div>
              </div>
            )}
          </button>
          <button
            type="button"
            onClick={toggleTheme}
            className="w-7 h-7 rounded grid place-items-center transition-colors"
            style={{ color: 'var(--text-muted)' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--bg-hover)';
              e.currentTarget.style.color = 'var(--text)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = 'var(--text-muted)';
            }}
            title={t('nav.toggle_theme')}
          >
            {resolvedTheme === 'dark' ? (
              <SunIcon className="w-4 h-4" />
            ) : (
              <MoonIcon className="w-4 h-4" />
            )}
          </button>
          <button
            type="button"
            onClick={handleLogout}
            className="w-7 h-7 rounded grid place-items-center transition-colors"
            style={{ color: 'var(--text-muted)' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--danger-bg)';
              e.currentTarget.style.color = 'var(--danger)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = 'var(--text-muted)';
            }}
            title={t('nav.logout')}
          >
            <LogoutIcon className="w-4 h-4" />
          </button>
        </div>
      </aside>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />

      {inviteTenant && (
        <InviteModal
          tenantId={inviteTenant.tenant_id}
          tenantName={inviteTenant.name}
          onClose={() => setInviteTenant(null)}
        />
      )}

      {createSpaceOpen && (
        <CreateSpaceModal
          onClose={() => setCreateSpaceOpen(false)}
          onCreated={(t) => {
            addTenant(t, /* switchTo */ true);
            setCreateSpaceOpen(false);
            navigate('/vault');
          }}
        />
      )}

      {/* ============ MAIN ============ */}
      <div className="flex flex-col overflow-hidden">
        {/* Topbar — stil codex-v6: 54px, breadcrumb cu icon accent glow,
             search input fake vizibil cu placeholder + kbd shortcut. */}
        <div
          className="flex items-center gap-3 px-3 md:px-5 flex-shrink-0"
          style={{
            height: 54,
            background: 'var(--bg-elevated)',
            borderBottom: '1px solid var(--border)',
          }}
        >
          {isMobile && (
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="grid place-items-center rounded-md flex-shrink-0 -ml-1.5"
              style={{
                width: 36,
                height: 36,
                color: 'var(--text-muted)',
                border: '1px solid transparent',
              }}
              aria-label={t('shell_ui.layout.open_menu')}
              title={t('shell_ui.layout.menu')}
            >
              <HamburgerIcon className="w-5 h-5" />
            </button>
          )}
          <div className="flex items-center gap-2.5 text-[13px] min-w-0">
            {currentNav && (
              <div
                className="w-6 h-6 rounded-md grid place-items-center flex-shrink-0"
                style={{
                  background: 'var(--accent-bg)',
                  color: 'var(--accent)',
                  boxShadow: 'var(--glow-mint)',
                }}
                aria-hidden
              >
                <currentNav.Icon className="w-3.5 h-3.5" />
              </div>
            )}
            <span
              className="font-serif"
              style={{
                color: 'var(--text-muted)',
                fontSize: 15,
                letterSpacing: '-0.01em',
              }}
            >
              {t('nav.navigation')}
            </span>
            <span style={{ color: 'var(--text-dim)' }}>/</span>
            <strong
              className="font-medium font-serif"
              style={{
                color: 'var(--text)',
                fontSize: 15,
                letterSpacing: '-0.01em',
              }}
            >
              {currentNav ? t(currentNav.labelKey) : t('app.name')}
            </strong>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <SessionTimer variant={isMobile ? 'compact' : 'full'} />
            {isMobile && (
              <button
                type="button"
                onClick={() => setPaletteOpen(true)}
                className="grid place-items-center rounded-md flex-shrink-0"
                style={{
                  width: 36,
                  height: 36,
                  color: 'var(--text-muted)',
                  border: '1px solid var(--border-strong)',
                  background: 'var(--bg)',
                }}
                aria-label={t('common.search')}
                title={t('shell_ui.layout.search_shortcut')}
              >
                <SearchIcon className="w-4 h-4" />
              </button>
            )}
            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              className="hidden md:flex items-center gap-2 rounded-md text-[12.5px] transition-colors"
              style={{
                background: 'var(--bg)',
                border: '1px solid var(--border-strong)',
                color: 'var(--text-dim)',
                padding: '7px 12px',
                minWidth: 320,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--accent)';
                e.currentTarget.style.background = 'var(--bg-surface)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border-strong)';
                e.currentTarget.style.background = 'var(--bg)';
              }}
            >
              <SearchIcon className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="flex-1 text-left">
                {t('shell_ui.layout.search_placeholder')}
              </span>
              <span className="kbd">⌘K</span>
            </button>
          </div>
        </div>

        <RecoveryReminder />

        {/* Content */}
        <main
          className={`flex-1 app-main-bg ${fullWidth ? 'overflow-hidden' : 'overflow-auto'}`}
        >
          {fullWidth ? (
            <Outlet key={currentTenantId ?? 'none'} />
          ) : (
            <div className="max-w-6xl mx-auto px-4 py-5 md:px-8 md:py-8">
              <Outlet key={currentTenantId ?? 'none'} />
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
